import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth'
import { PRIVATE_MODE, PRIVATE_AUTH_TOKEN } from './privateConfig'
import { LOCAL_MODE } from './backendConfig'

// ── Private mode: fake User object ─────────────────────────────

const PRIVATE_USER = {
  uid: 'private_user',
  email: PRIVATE_AUTH_TOKEN,
  emailVerified: true,
  displayName: 'Private User',
  isAnonymous: false,
  photoURL: null,
  providerData: [],
  metadata: {},
  phoneNumber: null,
  providerId: 'private',
  tenantId: null,
  refreshToken: '',
  delete: async () => {},
  getIdToken: async () => PRIVATE_AUTH_TOKEN,
  getIdTokenResult: async () => ({ token: PRIVATE_AUTH_TOKEN }) as any,
  reload: async () => {},
  toJSON: () => ({}),
} as unknown as User

// ── Local mode: fake User object ───────────────────────────────
// Local mode skips Google login but still uses the cloud-backed app against the
// local docker stack. The backend (ENABLE_TEST_MODE=true) accepts a `test_*`
// bearer token and resolves it to `<id>@test.com` (see services/authenticator.py),
// so this synthetic identity keeps per-user data/HipPa memory under a stable
// local account without any Firebase round-trip.
export const LOCAL_AUTH_TOKEN = 'test_localuser_token'
const LOCAL_USER_EMAIL = 'localuser@test.com'

const LOCAL_USER = {
  uid: 'localuser',
  email: LOCAL_USER_EMAIL,
  emailVerified: true,
  displayName: 'Local User',
  isAnonymous: false,
  photoURL: null,
  providerData: [],
  metadata: {},
  phoneNumber: null,
  providerId: 'local',
  tenantId: null,
  refreshToken: '',
  delete: async () => {},
  getIdToken: async () => LOCAL_AUTH_TOKEN,
  getIdTokenResult: async () => ({ token: LOCAL_AUTH_TOKEN }) as any,
  reload: async () => {},
  toJSON: () => ({}),
} as unknown as User

// Both modes bypass the Google sign-in popup. They differ in which synthetic
// identity/token is presented and (elsewhere) whether cloud features are on.
const AUTH_BYPASS = PRIVATE_MODE || LOCAL_MODE
const BYPASS_USER = LOCAL_MODE ? LOCAL_USER : PRIVATE_USER
const BYPASS_TOKEN = LOCAL_MODE ? LOCAL_AUTH_TOKEN : PRIVATE_AUTH_TOKEN

// ── Firebase init (skipped when auth is bypassed — config may be empty) ──

let auth: ReturnType<typeof getAuth> | null = null
let provider: GoogleAuthProvider | null = null

if (!AUTH_BYPASS) {
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  }
  const app = initializeApp(firebaseConfig)
  auth = getAuth(app)
  provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
}

// ── Exports ────────────────────────────────────────────────────

export async function signInWithGoogle(): Promise<User> {
  if (AUTH_BYPASS) return BYPASS_USER
  const result = await signInWithPopup(auth!, provider!)
  return result.user
}

export async function signOut(): Promise<void> {
  if (AUTH_BYPASS) return
  await firebaseSignOut(auth!)
}

export async function getIdToken(): Promise<string | null> {
  if (AUTH_BYPASS) return BYPASS_TOKEN
  const user = auth?.currentUser
  if (!user) return null
  return user.getIdToken()
}

export function onAuthStateChanged(callback: (user: User | null) => void): () => void {
  if (AUTH_BYPASS) {
    setTimeout(() => callback(BYPASS_USER), 0)
    return () => {}
  }
  return firebaseOnAuthStateChanged(auth!, callback)
}

export function getCurrentUser(): User | null {
  if (AUTH_BYPASS) return BYPASS_USER
  return auth?.currentUser ?? null
}
